import { Controller } from "@hotwired/stimulus"

// Recruiter-Review: Kategorie-Navigation links, fixer PDF-Viewer oben,
// Dokumententabelle darunter. Downloads zeigen DSGVO-Löschhinweis.
const LARGE_FILE_THRESHOLD_BYTES = 20 * 1024 * 1024 // 20 MB

export default class extends Controller {
  static targets = ["viewer", "filename", "empty", "row", "categoryBtn"]
  static values = { deletionDate: String }

  connect() {
    this.activateCategory("")
    this.openFirstPreviewable()
  }

  selectCategory(event) {
    event.preventDefault()
    const catId = String(event.currentTarget.dataset.categoryId || "")
    this.activateCategory(catId)
    this.openFirstPreviewable()
  }

  activateCategory(catId) {
    this.categoryBtnTargets.forEach((btn) => {
      btn.classList.toggle("is-active", String(btn.dataset.categoryId || "") === catId)
    })
    this.rowTargets.forEach((row) => {
      const match = catId === "" || String(row.dataset.categoryId || "") === catId
      row.classList.toggle("hidden", !match)
    })
    this.syncSectionVisibility()
  }

  syncSectionVisibility() {
    this.element.querySelectorAll("[data-docs-section]").forEach((section) => {
      const visible = Array.from(section.querySelectorAll("[data-applicant-docs-target='row']"))
        .some((r) => !r.classList.contains("hidden"))
      section.classList.toggle("hidden", !visible)
    })
  }

  view(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-applicant-docs-target='row']")
    if (row) this.activateRow(row, { userInitiated: true })
  }

  forceLoad(event) {
    event.preventDefault()
    const { url, filename } = event.currentTarget.dataset
    if (!url) return
    if (this.hasFilenameTarget) this.filenameTarget.textContent = filename || ""
    this.viewerTarget.src = url
    this.viewerTarget.classList.remove("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
  }

  openFirstPreviewable() {
    const first = this.rowTargets.find(
      (r) => !r.classList.contains("hidden") && r.dataset.previewable === "true"
    )
    if (first) {
      this.activateRow(first, { userInitiated: false })
    } else {
      this.clearViewer()
    }
  }

  activateRow(row, { userInitiated = false } = {}) {
    const url = row.dataset.previewUrl
    if (!url) return
    this.rowTargets.forEach((r) => r.classList.toggle("bg-blue-50", r === row))
    if (this.hasFilenameTarget) this.filenameTarget.textContent = row.dataset.filename || ""

    const size = parseInt(row.dataset.fileSize || "0", 10)
    if (!userInitiated && size > 0 && size > LARGE_FILE_THRESHOLD_BYTES) {
      this.renderLargeFilePrompt(row, size)
      return
    }

    this.viewerTarget.src = url
    this.viewerTarget.classList.remove("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
  }

  renderLargeFilePrompt(row, size) {
    const mb = (size / 1024 / 1024).toFixed(1)
    this.viewerTarget.src = "about:blank"
    this.viewerTarget.classList.add("hidden")
    if (!this.hasEmptyTarget) return
    this.emptyTarget.classList.remove("hidden")
    this.emptyTarget.classList.remove("text-gray-400", "text-sm")
    this.emptyTarget.innerHTML = `
      <div class="text-center px-4">
        <div class="text-gray-700 mb-1 font-medium">Großes PDF (${mb} MB)</div>
        <div class="text-xs text-gray-500 mb-3">Das Laden kann den Browser verlangsamen.</div>
        <button type="button"
                class="btn btn--default"
                data-action="click->applicant-docs#forceLoad"
                data-url="${row.dataset.previewUrl}"
                data-filename="${(row.dataset.filename || "").replace(/"/g, "&quot;")}">
          Trotzdem laden
        </button>
      </div>`
  }

  clearViewer() {
    this.viewerTarget.src = "about:blank"
    this.viewerTarget.classList.add("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.remove("hidden")
    if (this.hasFilenameTarget) this.filenameTarget.textContent = ""
  }

  confirmDownload(event) {
    const date = this.deletionDateValue
    const deletionLine = date
      ? `Das Dokument ist spätestens am ${date} lokal zu löschen (Löschdatum des Bewerbers).`
      : "Das Dokument ist lokal zu löschen, sobald das Löschdatum des Bewerbers erreicht ist."
    const msg = `Datenschutz-Hinweis\n\n${deletionLine}\n\nJetzt herunterladen?`
    if (!confirm(msg)) event.preventDefault()
  }
}
